import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { Popover, Overlay } from "react-bootstrap";
import * as App from "redux-store/applications/selectors";
import Asset from "ui/asset";
import { sortByProp } from "util/sort-by-prop";

const BLACK_SCREEN_NAME = "Black Screen";
const BLACK_SCREEN_APP_ID = "__bs";

class ApplicationViewChart extends React.PureComponent {
  static get propTypes() {
    return {
      applications: PropTypes.object.isRequired,
      data: PropTypes.array.isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      showPopoverIdx: null,
    };

    this._hideTimeout = null;
    this._hideIdx = null;
    this.assetRefs = [];
    this.popoverRefs = [];

    this._renderImage = this._renderImage.bind(this);
    this._sortByXDesc = sortByProp.bind(null, "x", "desc");
    this._viewDataToAsset = this._viewDataToAsset.bind(this);
    this._showPopover = this._showPopover.bind(this);
    this._hidePopover = this._hidePopover.bind(this);
  }

  _getAppName(props, appId) {
    const { applications } = props;

    if (appId === BLACK_SCREEN_APP_ID) {
      return BLACK_SCREEN_NAME;
    }

    const app = App.getByApplicationName(applications, appId);

    return app ? App.name(app) : appId;
  }

  _renderPopoverContent(assetData) {
    if (assetData.name === BLACK_SCREEN_NAME) {
      return <span>Black screen is visible for only 1 second</span>;
    }

    const url = assetData.label ? assetData.label.toLowerCase() : "";
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return assetData.label;
    }

    return (
      <a target="_blank" rel="noopener noreferrer" href={assetData.label}>
        Download Asset
      </a>
    );
  }

  _showPopover(idx) {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
      this._hideIdx = null;
    }

    this.setState({ showPopoverIdx: idx });
  }

  _hidePopover(idx) {
    this._hideIdx = idx;

    this._hideTimeout = setTimeout(() => {
      if (this._hideIdx !== idx) return;

      const assetHovered =
        this.assetRefs[idx] &&
        this.assetRefs[idx].matches &&
        this.assetRefs[idx].matches(":hover");

      const popoverHovered =
        this.popoverRefs[idx] &&
        this.popoverRefs[idx].matches &&
        this.popoverRefs[idx].matches(":hover");

      if (!assetHovered && !popoverHovered) {
        this.setState({ showPopoverIdx: null });
      }
    }, 300);
  }

  _renderOverlay(assetData, idx) {
    const popoverId = "popover-" + idx;

    return (
      <Popover
        id={popoverId}
        key={idx}
        ref={(elem) => {
          this.popoverRefs[idx] = elem;
        }}
        onMouseEnter={() => this._showPopover(idx)}
        onMouseLeave={() => this._hidePopover(idx)}
      >
        <Popover.Title as="h3">{assetData.name}</Popover.Title>
        <Popover.Content>
          {this._renderPopoverContent(assetData)}
          <br />
          {moment.utc(assetData.x).local().format("MMMM Do YYYY, h:mm:ss a")}
        </Popover.Content>
      </Popover>
    );
  }

  _renderImage(assetData, idx) {
    const popover = this._renderOverlay(assetData, idx);
    const { applications } = this.props;
    const currentApp = App.getByApplicationName(applications, assetData.appId);
    const appPlaceholder = currentApp ? App.icon(currentApp) : "";
  
    return (
      <li
        key={assetData.name + "-" + assetData.x}
        className="status-image-item"
        ref={(el) => {
          this.assetRefs[idx] = el;
        }}
        onMouseEnter={() => this._showPopover(idx)}
        onMouseLeave={() => this._hidePopover(idx)}
      >
        <Asset
          className="asset-horizontal"
          url={assetData.label}
          blackscreen={assetData.name === BLACK_SCREEN_NAME}
          placeholder={appPlaceholder}
          preload="none"
        />

        <Overlay
          target={this.assetRefs[idx]}
          show={this.state.showPopoverIdx === idx}
          placement="top"
          containerPadding={10}
        >
          {popover}
        </Overlay>
      </li>
    );
  }

  _viewDataToAsset(viewData) {
    const [ts, appId, label = ""] = viewData;
    const name = this._getAppName(this.props, appId);

    return {
      appId,
      label,
      name,
      x: ts,
      y: 1,
    };
  }

  render() {
    const { data } = this.props;

    if (data.length === 0) {
      return <div>No Data.</div>;
    }

    const viewAssets = data
      .map(this._viewDataToAsset)
      .sort(this._sortByXDesc)
      .map(this._renderImage);

    return (
      <div className="status-images-wrapper">
        <ul className="status-image-list">{viewAssets}</ul>
      </div>
    );
  }
}

export { ApplicationViewChart };
